import React from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import CustomScrollbar from 'components/Common/CustomScrollbar';

const StakeholderPageSkeleton = () => {
  const [heightContainer, containerRef] = useHeightContainer();

  return (
    <SkeletonTheme baseColor='#f2f2f2'>
      <div ref={containerRef} style={{ height: `${heightContainer}px` }}>
        <div className='d-lg-flex align-items-start'>
          <div className='biography-general-container'>
            <CustomScrollbar
              className={'simple-scrollbar profile-scrollbar'}
              style={{ height: `${heightContainer}px` }}
              maximalThumbYSize={100}
            >
              <div className='pr-4'>
                <BioSkeleton />
              </div>
            </CustomScrollbar>
          </div>
          <div className='flex-grow-1'>
            <RightSideActivitySkeleton />
          </div>
        </div>
      </div>
    </SkeletonTheme>
  );
};

const BioSkeleton = () => {
  return (
    <div className={`box-top-rounded rounded`}>
      <div className='d-flex align-items-center px-4 py-3'>
        <div className='text-center'>
          <Skeleton width={110} height={110} circle={true} />
        </div>
        <div className='col-13'>
          <Skeleton height={20} width={'80%'} />
          <div className='my-1'>
            <Skeleton height={20} width={'70%'} />
          </div>
        </div>
      </div>

      <div className='flex-centered justify-content-lg-between px-4 overflow-hidden position-relative gap-15 pt-4 border-top'>
        <Skeleton height={37} width={'116px'} borderRadius={9999} />
        <Skeleton height={37} width={'116px'} borderRadius={9999} />
        <Skeleton height={37} width={'129px'} borderRadius={9999} />
        <Skeleton height={37} width={'121px'} borderRadius={9999} />
      </div>

      <div className={`py-3 section-title px-4`}>
        {Array(3)
          .fill()
          .map((item, key) => (
            <div className={`py-3 `} key={`item-text-${key}`}>
              <div className='my-1'>
                <Skeleton height={19} width={'50%'} />
              </div>
              <div className='my-1'>
                <Skeleton height={19} width={'30%'} />
              </div>
            </div>
          ))}
      </div>

      {Array(3)
        .fill()
        .map((item, key, array) => (
          <div
            className={` px-4 py-3 ${key === array.length - 1 ? 'rounded-bottom' : 'section-title'}`}
            key={`item-infor-${key}`}
          >
            <div className='my-1'>
              <Skeleton height={19} width={'70%'} />
            </div>
            <div className='my-1'>
              <Skeleton height={19} width={'50%'} />
            </div>
          </div>
        ))}
    </div>
  );
};

const ActivitySkeleton = () => {
  return (
    <SkeletonTheme baseColor='#f2f2f2'>
      <ActivitySingleContentTypeSkeleton withTheme={false} />
      <ActivitySingleContentTypeSkeleton withTheme={false} titleContainerClassName='my-2' />
    </SkeletonTheme>
  );
};

// Skeleton for one activity content type section: title + 3 list rows.
const ActivitySingleContentTypeSkeleton = ({ titleContainerClassName = 'mt-2 mb-4', withTheme = true } = {}) => {
  const content = (
    <>
      <div className={titleContainerClassName}>
        <Skeleton height={24} width={'50%'} />
      </div>
      <ul className='policy-list px-0 items-list'>
        {Array(3)
          .fill()
          .map((item, index) => (
            <li className={`my-2`} key={index}>
              <div className='row mx-0 p-3 align-items-center'>
                <div className='mr-3'>
                  <Skeleton height={19} width={19} />
                </div>
                <div className='col-12 col-sm-13 pr-lg-5 pr-2 pl-0'>
                  <Skeleton height={19} width={`80%`} />
                  <Skeleton height={19} width={`30%`} />
                </div>
                <div className='col-4 col-sm-2 d-none d-md-block'>
                  <Skeleton height={13} width={`100%`} />
                  <div className='mx-auto d-flex flex-column align-items-center'>
                    <Skeleton height={13} width={`65px`} />
                  </div>
                </div>
              </div>
            </li>
          ))}
      </ul>
    </>
  );

  if (!withTheme) return content;

  return <SkeletonTheme baseColor='#f2f2f2'>{content}</SkeletonTheme>;
};

const ActivityHeaderSkeleton = () => {
  return (
    <SkeletonTheme baseColor='#f2f2f2'>
      <div className='justify-content-between row mx-0 align-items-center py-3 py-lg-4 border-bottom px-4 px-lg-5'>
        <div className='col-lg-6 px-0'>
          <Skeleton height={28} width={'70%'} />
        </div>
        <div className='col-lg-6 px-0 d-flex justify-content-lg-end mt-3 mt-lg-0'>
          <Skeleton height={36} borderRadius={9999} width={'420px'} />
        </div>
      </div>
    </SkeletonTheme>
  );
};

const ActivityTabsSkeleton = () => {
  return (
    <SkeletonTheme baseColor='#f2f2f2'>
      <div className='w-100 overflow-hidden scrollbar-right-fade'>
        <div className='flex-centered pb-4 specific-top-padding-22 gap-15 flex-nowrap activity-tabs-container w-100'>
          <div>
            <Skeleton height={37} width={131} borderRadius={9999} />
          </div>
          <div>
            <Skeleton height={37} width={155} borderRadius={9999} />
          </div>
          <div>
            <Skeleton height={37} width={202} borderRadius={9999} />
          </div>
          <div>
            <Skeleton height={37} width={140} borderRadius={9999} />
          </div>
        </div>
      </div>
    </SkeletonTheme>
  );
};

const RightSideActivitySkeleton = () => {
  return (
    <div className='box-rounded'>
      <ActivityHeaderSkeleton />
      <div className='px-4 px-lg-5'>
        <ActivityTabsSkeleton />
        <ActivitySkeleton />
      </div>
    </div>
  );
};

export { BioSkeleton };
export { ActivitySkeleton };
export { ActivitySingleContentTypeSkeleton };
export { ActivityHeaderSkeleton };
export { ActivityTabsSkeleton };
export { RightSideActivitySkeleton };
export default StakeholderPageSkeleton;
